
import axios from 'axios';
import { toast } from 'sonner';

// API Endpoints
const VERIFICATION_WEBHOOK = 'https://hook.us1.make.com/9olzm0evgy1m3jb5iu31cezgo66cvuwp';
const SIGNUP_WEBHOOK = 'https://hook.us1.make.com/7q7j2vye33n92bdjug3v5fxccif35hbc';

// Maximum retry attempts for API calls
const MAX_RETRIES = 3;
// Timeout for API calls in milliseconds
const API_TIMEOUT = 15000; // Increased timeout

// Generate a random 4-digit code
export const generateVerificationCode = (): string => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

// Improved retry logic with exponential backoff
const callWithRetry = async (apiCall: () => Promise<any>, retries = MAX_RETRIES, initialDelayMs = 1000) => {
  let currentRetry = 0;
  let delayMs = initialDelayMs;

  while (true) {
    try {
      return await apiCall();
    } catch (error: any) {
      currentRetry++;
      
      // If no more retries or not a retryable error, throw
      if (currentRetry > retries || 
          (error.response && error.response.status < 500 && error.response.status !== 429)) {
        console.error(`API call failed after ${currentRetry} attempts:`, error);
        throw error;
      }
      
      console.log(`API call failed, retrying in ${delayMs}ms... (${retries - currentRetry + 1} attempts left)`);
      
      // Show toast for retry
      toast.error(`Connection issue. Retrying in ${delayMs/1000} seconds...`);
      
      // Wait with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delayMs));
      
      // Increase delay for next retry (exponential backoff with jitter)
      delayMs = Math.min(delayMs * 2, 10000) * (0.8 + Math.random() * 0.4);
    }
  }
};

// Ensure phone is formatted to contain only digits
const formatUserData = (userData: any) => {
  if (!userData) return userData;
  
  // Create a shallow copy to avoid mutating the original
  const formattedData = { ...userData };
  
  // Ensure phone is numeric only
  if (formattedData.phone) {
    formattedData.phone = formattedData.phone.replace(/\D/g, '');
    console.log("Formatted phone number:", formattedData.phone);
  }
  
  return formattedData;
};

// Send verification code to webhook
export const sendVerificationCode = async (userData: any, code: string) => {
  if (!navigator.onLine) {
    toast.error("You are offline. Please check your internet connection.");
    throw new Error("You are offline. Please check your internet connection.");
  }
  
  try {
    console.log("Sending verification code...");
    // Format the data
    const formattedData = formatUserData(userData);
    
    return await callWithRetry(() => 
      axios.post(VERIFICATION_WEBHOOK, {
        ...formattedData,
        verificationCode: code
      }, {
        timeout: API_TIMEOUT,
        headers: {
          'Content-Type': 'application/json'
        }
      })
    );
  } catch (error: any) {
    console.error('Error sending verification code:', error);
    
    // Handle specific error types
    if (error.code === 'ECONNABORTED') {
      toast.error("Connection timed out. Please try again.");
      throw new Error("Connection timed out. Please try again.");
    }
    
    if (error.response) {
      // Server responded with error
      toast.error(`Server error: ${error.response.status}`);
      throw new Error(`Server error: ${error.response.status}`);
    }
    
    // Network error
    toast.error("Network error. Please check your connection and try again.");
    throw error;
  }
};

// Send user signup data to webhook
export const sendSignupData = async (userData: any) => {
  if (!navigator.onLine) {
    toast.error("You are offline. Please check your internet connection.");
    throw new Error("You are offline. Please check your internet connection.");
  }
  
  try {
    console.log("Sending signup data...");
    // Format the data
    const formattedData = formatUserData(userData);
    
    return await callWithRetry(() =>
      axios.post(SIGNUP_WEBHOOK, formattedData, {
        timeout: API_TIMEOUT,
        headers: {
          'Content-Type': 'application/json'
        }
      })
    );
  } catch (error: any) {
    console.error('Error sending signup data:', error);
    
    // Handle specific error types
    if (error.code === 'ECONNABORTED') {
      toast.error("Connection timed out. Please try again.");
      throw new Error("Connection timed out. Please try again.");
    }
    
    if (error.response) {
      toast.error(`Server error: ${error.response.status}`);
      throw new Error(`Server error: ${error.response.status}`);
    }
    
    toast.error("Network error. Please check your connection and try again.");
    throw error;
  }
};

// Resend verification code to webhook
export const resendVerificationCode = async (userData: any, code: string) => {
  return sendVerificationCode(userData, code);
};

// General purpose function to call any webhook
export const callWebhook = async (url: string, data: any) => {
  if (!navigator.onLine) {
    toast.error("You are offline. Please check your internet connection.");
    throw new Error("You are offline. Please check your internet connection.");
  }
  
  try {
    console.log(`Calling webhook ${url}...`);
    
    return await callWithRetry(() =>
      axios.post(url, data, {
        timeout: API_TIMEOUT,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
    );
  } catch (error: any) {
    console.error(`Error calling webhook ${url}:`, error);
    
    if (error.code === 'ECONNABORTED') {
      toast.error("Connection timed out. Please try again.");
      throw new Error("Connection timed out. Please try again.");
    }
    
    if (error.response) {
      toast.error(`Server error: ${error.response.status}`);
      throw new Error(`Server error: ${error.response.status}`);
    }
    
    toast.error("Network error. Please check your connection and try again.");
    throw error;
  }
};
